import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import type { CreateAreaChargeDto, UpdateAreaChargeDto } from '@carvia/ros-client-types';
import type { MaybeRefOrGetter } from 'vue';
import { computed, toValue } from 'vue';
import { areaChargesService } from '@/api/area-charges.service.ts';
import type { MutationDefaultsContext } from '@/queries/query-client.ts';

const QUERY_KEY = 'area-charges';

export const useAreaCharges = () => {
  return useQuery({
    queryKey: [QUERY_KEY],
    queryFn: () => areaChargesService.getAll(),
  });
};

export const useAreaCharge = (id: MaybeRefOrGetter<string | undefined>) => {
  return useQuery({
    queryKey: [QUERY_KEY, id],
    queryFn: () => areaChargesService.getOne(toValue(id)!),
    enabled: computed(() => !!toValue(id)),
  });
};

export const useCreateAreaCharge = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreateAreaChargeDto) => areaChargesService.create(dto),
    onMutate: (): MutationDefaultsContext => ({
      successMessage: 'Area charge created',
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [QUERY_KEY] }),
  });
};

export const useUpdateAreaCharge = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateAreaChargeDto }) =>
      areaChargesService.update(id, dto),
    onMutate: (): MutationDefaultsContext => ({
      successMessage: 'Area charge updated',
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [QUERY_KEY] }),
  });
};

export const useDeleteAreaCharge = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => areaChargesService.remove(id),
    onMutate: (): MutationDefaultsContext => ({
      successMessage: 'Area charge deleted',
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [QUERY_KEY] }),
  });
};
